'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState, useRef } from 'react';
import { useForm, useFieldArray, FormProvider, useFormContext } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Lenis from 'lenis';
import { toast } from 'sonner';
import AnchorMenu from '@/src/components/common/header/AnchorMenu';
import TermsSection from '@/src/components/forms/TermsSection';
import { cx } from '@/src/styles/stylebook';
import FlexGridLayout, { FlexGridItem } from '@/src/layouts/FlexGridLayout';
import Multilingual from '../global/Multilingual';
import useStorage from '@/src/hooks/useStorage';

// TermItem is now imported from TermsSection

function buildZodField(field) {
  const base = (() => {
    switch (field.type) {
      case 'string':
        return z.string();
      case 'text':
        return z.string();
      case 'url':
        return z.string().url('유효한 URL 형식이 아닙니다');
      case 'password':
        return z.string();
      case 'phone':
        return z.string();
      case 'radio':
      case 'checkbox':
        return z.any();
      case 'array':
        return z.array(z.object(Object.fromEntries((field.fields || []).map(f => [f.key, buildZodField(f)])))).min(1, `${field.label || '항목'}은(는) 최소 1개 이상 필요합니다`);
      default:
        return z.any();
    }
  })();
  const isStringy = ['string', 'text', 'url', 'password', 'phone'].includes(field.type);
  // String-like: apply min first (if required), then regex; keep as ZodString
  if (isStringy) {
    let s = base;

    // Use validation pattern from schema
    const validationPattern = field.validation;
    if (field.required) {
      s = s.min(1, `${field.label}은(는) 필수입니다`);
      // Apply validation pattern for required fields
      if (validationPattern) {
        s = s.regex(new RegExp(validationPattern), '형식이 올바르지 않습니다');
      }
    } else {
      // For optional fields: empty string OR valid pattern
      if (validationPattern) {
        const refinedSchema = s.refine(val => val === '' || new RegExp(validationPattern).test(val), {
          message: '형식이 올바르지 않습니다'
        });
        return refinedSchema.optional();
      }
      return s.optional();
    }
    return s;
  }
  let schema = base;
  if (field.required) {
    if (field.type === 'array') {
      return schema.min(1, `${field.label}은(는) 필수입니다`);
    }
    // For non-stringy non-array (radio/checkbox), require defined value
    return schema.refine(v => v !== undefined && v !== null, `${field.label}은(는) 필수입니다`);
  }
  return schema.optional();
}
function SectionFields(t0) {
  const $ = _c(20);
  const {
    section
  } = t0;
  const {
    register,
    formState: t1
  } = useFormContext();
  const {
    errors
  } = t1;
  const t2 = section.key;
  let t3;
  if ($[0] !== section.title) {
    t3 = <h5 className="h5 px-marg pb-2.5"><Multilingual>{section.title}</Multilingual></h5>;
    $[0] = section.title;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== section.description) {
    t4 = section.description && <p className="px-marg mb-4 opacity-60">{section.description}</p>;
    $[2] = section.description;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== errors || $[5] !== register || $[6] !== section.fields || $[7] !== section.key) {
    let t6;
    if ($[9] !== errors || $[10] !== register || $[11] !== section.key) {
      t6 = (field, fieldIndex) => {
        if (field.type === "array") {
          return <ArrayField key={field.key} sectionKey={section.key} field={field} />;
        }
        return <FlexGridLayout key={field.key} className="odd:bg-system-tilebg py-3 max-md:py-2"><FlexGridItem cols={2} className="h6 max-md:w-grid-2 max-md:p text-pretty max-md:shrink-0"><Multilingual>{field.label}{field.required ? "*" : ""}</Multilingual></FlexGridItem><FlexGridItem cols={10} className="h6 max-md:w-grid-4 max-md:p0 font-medium! *:w-full">{field.type === "text" ? <textarea className="input max-md:p resize-none" placeholder={field.placeholder} {...register("sections." + section.key + "." + field.key)} /> : field.type === "radio" ? <div className="flex flex-col flex-wrap gap-4">{(field.options || []).map(opt => <label key={opt.value} className="h6 max-md:p gap-2"><input type="radio" value={opt.value} className="peer hidden" {...register("sections." + section.key + "." + field.key)} /><span className={cx("text-bg-box px-gutter peer-checked:bg-gray-0 py-2 break-keep select-none peer-checked:text-green-50", fieldIndex % 2 === 0 ? "bg-gray-100" : "bg-system-tilebg")}>{opt.label}</span></label>)}</div> : field.type === "checkbox" ? <div className="flex flex-col gap-4">{(field.options || []).map(opt_0 => <label key={opt_0.value} className="h6 max-md:p gap-2"><input type="checkbox" value={opt_0.value} className="peer hidden" {...register("sections." + section.key + "." + field.key)} /><span className={cx("text-bg-box px-gutter peer-checked:bg-gray-0 py-2 break-keep select-none peer-checked:text-green-50", fieldIndex % 2 === 0 ? "bg-gray-100" : "bg-system-tilebg")}>{opt_0.label}</span></label>)}</div> : <input className="input max-md:p" type={field.type === "password" ? "password" : "text"} placeholder={field.placeholder} {...register("sections." + section.key + "." + field.key)} />}{Boolean(errors?.sections?.[section.key]?.[field.key]) && <span className="smallp text-red-500">{errors?.sections?.[section.key]?.[field.key]?.message}</span>}</FlexGridItem></FlexGridLayout>;
      };
      $[9] = errors;
      $[10] = register;
      $[11] = section.key;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    t5 = section.fields.map(t6);
    $[4] = errors;
    $[5] = register;
    $[6] = section.fields;
    $[7] = section.key;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = <div>{t5}</div>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== section.key || $[16] !== t3 || $[17] !== t4 || $[18] !== t6) {
    t7 = <div id={t2} className="py-gutter max-md:pb-20">{t3}{t4}{t6}</div>;
    $[15] = section.key;
    $[16] = t3;
    $[17] = t4;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function ArrayField(t0) {
  const $ = _c(39);
  const {
    field,
    sectionKey
  } = t0;
  const {
    control,
    register,
    formState: t1
  } = useFormContext();
  const {
    errors
  } = t1;
  const label = field.label || "\uD56D\uBAA9";
  let t2;
  if ($[0] !== field.fields) {
    t2 = field.fields || [];
    $[0] = field.fields;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const childFields = t2;
  const getDefaultValue = _temp;
  const t3 = `sections.${sectionKey}.${field.key}`;
  let t4;
  if ($[2] !== control || $[3] !== t3) {
    t4 = {
      name: t3,
      control
    };
    $[2] = control;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    fields,
    append,
    remove
  } = useFieldArray(t4);
  const initialized = useRef(false);
  let t5;
  let t6;
  if ($[5] !== append || $[6] !== childFields || $[7] !== fields.length) {
    t5 = () => {
      if (!initialized.current && fields.length === 0) {
        initialized.current = true;
        append(Object.fromEntries(childFields.map(f => [f.key, getDefaultValue(f.type)])));
      }
    };
    t6 = [fields.length, append, childFields];
    $[5] = append;
    $[6] = childFields;
    $[7] = fields.length;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  if ($[10] !== childFields || $[11] !== errors || $[12] !== field.key || $[13] !== fields || $[14] !== label || $[15] !== register || $[16] !== remove || $[17] !== sectionKey) {
    let t8;
    if ($[19] !== childFields || $[20] !== errors || $[21] !== field.key || $[22] !== label || $[23] !== register || $[24] !== remove || $[25] !== sectionKey) {
      t8 = (row, idx) => <div key={row.id} className=""><div className="px-marg mb-2 flex items-center justify-between"><h5 className=""><Multilingual>{label} {idx + 1}</Multilingual></h5><button type="button" className="h5 text-system-error" onClick={() => remove(idx)}>삭제</button></div><div className="grid gap-2">{childFields.map(cf => <FlexGridLayout key={cf.key} className="odd:bg-system-tilebg h6 max-md:p py-3 max-md:py-2"><FlexGridItem cols={2} className="text-pretty"><Multilingual>{cf.label}{cf.required ? "*" : ""}</Multilingual></FlexGridItem><FlexGridItem cols={10} className="max-md:w-grid-4 font-medium! *:w-full">{cf.type === "text" ? <textarea className="input max-md:p resize-none" placeholder={cf.placeholder} {...register(`sections.${sectionKey}.${field.key}.${idx}.${cf.key}`)} /> : cf.type === "radio" ? <div className="flex flex-col flex-wrap gap-4">{(cf.options || []).map(opt => <label key={opt.value} className="h6 max-md:p gap-2"><input type="radio" value={opt.value} className="peer hidden" {...register(`sections.${sectionKey}.${field.key}.${idx}.${cf.key}`)} /><span className="text-bg-box px-gutter bg-system-tilebg peer-checked:bg-gray-0 py-2 break-keep select-none peer-checked:text-green-50">{opt.label}</span></label>)}</div> : cf.type === "checkbox" ? <div className="flex flex-col gap-4">{(cf.options || []).map(opt_0 => <label key={opt_0.value} className="h6 max-md:p gap-2"><input type="checkbox" value={opt_0.value} className="peer hidden" {...register(`sections.${sectionKey}.${field.key}.${idx}.${cf.key}`)} /><span className="text-bg-box px-gutter bg-system-tilebg peer-checked:bg-gray-0 py-2 break-keep select-none peer-checked:text-green-50">{opt_0.label}</span></label>)}</div> : <input className="input max-md:p" type={cf.type === "password" ? "password" : "text"} placeholder={cf.placeholder} {...register(`sections.${sectionKey}.${field.key}.${idx}.${cf.key}`)} />}{Boolean(errors?.sections?.[sectionKey]?.[field.key]?.[idx]?.[cf.key]) && <span className="smallp text-red-500">{errors?.sections?.[sectionKey]?.[field.key]?.[idx]?.[cf.key]?.message}</span>}</FlexGridItem></FlexGridLayout>)}</div></div>;
      $[19] = childFields;
      $[20] = errors;
      $[21] = field.key;
      $[22] = label;
      $[23] = register;
      $[24] = remove;
      $[25] = sectionKey;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    t7 = fields.map(t8);
    $[10] = childFields;
    $[11] = errors;
    $[12] = field.key;
    $[13] = fields;
    $[14] = label;
    $[15] = register;
    $[16] = remove;
    $[17] = sectionKey;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[27] !== t7) {
    t8 = <div className="flex flex-col gap-4">{t7}</div>;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cx("text-gray-0 bold h3 disabled:bg-gray-90 disabled:text-gray-70 hover:bg-gray-0 mx-auto flex items-center justify-center rounded-full bg-green-50 px-30 py-4 pb-5 text-center hover:text-green-50", "max-md:h5 max-md:min-w-[220px] max-md:px-8 max-md:py-3");
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== append || $[31] !== childFields) {
    t10 = () => append(Object.fromEntries(childFields.map(f_0 => [f_0.key, getDefaultValue(f_0.type)])));
    $[30] = append;
    $[31] = childFields;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const t11 = `${label} `;
  let t12;
  if ($[33] !== t10 || $[34] !== t11) {
    t12 = <div className="px-marg"><button type="button" className={t9} onClick={t10}>{t11}추가</button></div>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t12 || $[37] !== t8) {
    t13 = <div className="flex flex-col gap-3">{t8}{t12}</div>;
    $[36] = t12;
    $[37] = t8;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
}
function _temp(fieldType) {
  switch (fieldType) {
    case "radio":
      {}
    case "checkbox":
      {
        return;
      }
    default:
      {
        return "";
      }
  }
}
export default function SubmissionForm({
  data
}) {
  const [step, setStep] = useState('terms');
  const [showTermsErrors, setShowTermsErrors] = useState(false);
  const router = useRouter();
  const {
    setItem
  } = useStorage();
  const anchors = useMemo(() => data.sections.map(s => ({
    id: s.key,
    label: s.title
  })), [data.sections]);
  const schema = useMemo(() => {
    const sectionShape = {};
    for (const sec of data.sections) {
      const fieldsShape = {};
      for (const f of sec.fields) fieldsShape[f.key] = buildZodField(f);
      sectionShape[sec.key] = z.object(fieldsShape);
    }
    return z.object({
      terms: z.record(z.boolean()),
      sections: z.object(sectionShape)
    });
  }, [data.sections]);
  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      terms: {},
      sections: {}
    }
  });
  const {
    handleSubmit,
    register,
    watch
  } = methods;
  const termsState = watch('terms');
  const allTermsAccepted = data.terms.every(t => t.required ? termsState?.[t.key] : true);
  const onSubmit = async values => {
    const termsAccepted = data.terms.map(t_0 => ({
      key: t_0.key,
      accepted: Boolean(values.terms?.[t_0.key])
    }));
    const res = await fetch('/api/submission', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        termsAccepted,
        sections: values.sections
      })
    });
    const json = await res.json();
    if (!json.ok) {
      toast.error(json.error || '제출 실패');
      return;
    }

    // showResultPage가 true인 필드들만 필터링하여 결과 데이터 생성
    const filteredSections = {};
    const filteredFormSettings = {
      sections: []
    };
    for (const section of data.sections) {
      const filteredFields = {};
      const fieldsToShow = [];
      for (const field of section.fields) {
        if (field.showResultPage) {
          filteredFields[field.key] = values.sections?.[section.key]?.[field.key];
          fieldsToShow.push(field);
        }
      }
      if (Object.keys(filteredFields).length > 0) {
        filteredSections[section.key] = filteredFields;
        filteredFormSettings.sections.push({
          ...section,
          fields: fieldsToShow
        });
      }
    }

    // 제출 결과를 sessionStorage에 저장 (showResultPage가 true인 필드들만)
    const submissionResult = {
      sections: filteredSections,
      formSettings: filteredFormSettings
    };
    const saveResult = setItem('submissionResult', JSON.stringify(submissionResult), 'session');
    if (saveResult) {
      // 결과 페이지로 리다이렉트
      router.push('/submit/result');
    } else {
      toast.error('결과 저장에 실패했습니다.');
    }
  };
  useEffect(() => {
    const lenis = new Lenis();
    return () => {
      if (lenis?.destroy) lenis.destroy();
    };
  }, []);
  useEffect(() => {
    // Always reset to top when the step changes.
    window.scrollTo({
      top: 0,
      left: 0
    });
  }, [step]);
  return <FormProvider {...methods}>
      <form className="mb-50 flex flex-col gap-8 max-md:pt-[var(--headerh)]" onSubmit={handleSubmit(onSubmit)}>
        {step === 'terms' ? <div className="py-gutter">
            <TermsSection terms={data.terms} title="출품하기" showErrors={showTermsErrors} variant="default" />
            <div className="px-marg mt-6">
              <button type="button" className={cx('text-gray-0 bold h3 disabled:bg-gray-90 disabled:text-gray-70 flex w-full items-center justify-center rounded-full bg-green-50 py-4 pb-5 text-center')} disabled={!allTermsAccepted} onClick={() => {
            if (!allTermsAccepted) {
              setShowTermsErrors(true);
              return;
            }
            setStep('form');
          }}>
                다음 단계로
              </button>
            </div>
          </div> : <div>
            <AnchorMenu items={anchors} />
            {data.sections.map(section_0 => <SectionFields key={section_0.key} section={section_0} />)}
            <div className="px-marg py-gutter">
              <button type="submit" className={cx('text-gray-0 bold h3 disabled:bg-gray-90 disabled:text-gray-70 hover:bg-gray-0 mx-auto flex w-full items-center justify-center rounded-full bg-green-50 px-30 py-4 pb-5 text-center hover:text-green-50', 'max-md:h5 max-md:min-w-[220px]')}>
                제출하기
              </button>
            </div>
          </div>}
      </form>
    </FormProvider>;
}